'use client';

import { Bug, ArrowRight } from 'lucide-react';
import { trackCtaClick } from '@/lib/analytics';

/**
 * GlitchReplay product CTA — funnels InventiveHQ traffic to GlitchReplay
 * (glitchreplay.com), Sean's error-tracking + session-replay SaaS.
 *
 * Targets the AI-coding-assistant KB cluster (Claude Code, Codex, Copilot,
 * Gemini): developers who ship AI-generated code and need to catch what it
 * breaks in production.
 *
 * Bundle-safe: uses a plain <a> tag (NOT next/link) so it can be imported into
 * R2-bundled tools as well. See CLAUDE.md "R2 Tool Bundle System".
 *
 * Destination + UTM are centralized in buildGlitchReplayUrl() — the single
 * swap point if we later re-route to a different landing/signup URL.
 */

const GLITCHREPLAY_BASE = 'https://glitchreplay.com';
const LANDING_PATH = '/for/ai-assisted-development';

interface GlitchReplayCTAProps {
  /** Source page slug, for utm_content + click tracking. */
  source: string;
  /** Tighter spacing when embedded mid-result / in a sidebar card. */
  compact?: boolean;
}

/** Centralized destination builder — swap this to re-route all GlitchReplay CTAs. */
export function buildGlitchReplayUrl(source: string): string {
  const params = new URLSearchParams({
    utm_source: 'inventivehq',
    utm_medium: 'kb_cta',
    utm_campaign: 'ai-code-errors',
    utm_content: source,
  });
  return `${GLITCHREPLAY_BASE}${LANDING_PATH}?${params.toString()}`;
}

export default function GlitchReplayCTA({ source, compact }: GlitchReplayCTAProps) {
  const href = buildGlitchReplayUrl(source);

  return (
    <div
      className={`${compact ? 'p-4' : 'p-5'} rounded-lg border border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 to-sky-50`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
            <Bug className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400 mb-1">
            Shipping code with AI?
          </div>
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1">
            Get alerted when it breaks
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            AI assistants ship code you didn&apos;t write line-by-line. GlitchReplay
            gives you error tracking plus session replay — so when AI-generated code
            breaks in production, you see the exact stack trace and the user&apos;s
            screen. Sentry-SDK compatible, flat-rate pricing.
          </p>
          <a
            href={href}
            target="_blank"
            rel="nofollow noopener noreferrer"
            onClick={() => trackCtaClick('GlitchReplay: ai-code-errors', 'blog_cta', href)}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white px-4 py-2 rounded-md font-medium text-sm hover:shadow-lg transition-all"
          >
            Try GlitchReplay free
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
